// Activate Account — Step 2: Avatar Upload
// ------------------------------------------
// Allows a new user to upload a profile picture during account activation.
// The file is uploaded directly to S3 via a presigned PUT URL.
// Tailwind-styled, functional component.

import React, { useState, useCallback, useRef, useEffect } from "react";
import { Loader2, Upload } from "lucide-react";
import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";

import { Button } from "@/components/ui/button";
import CognitoLoginClass from "@/models/cognitoLogin";
import { getFileUploadUrl, changeAvatar } from "@/services/graphql/mutations";
import { getGraphData } from "@/utils/transform";
import { AVATAR_UPLOAD_HINT } from "@/constants/definitions";

// Force Cognito User Pools auth on every GraphQL call.
const graphqlOperation = (query: any, variables?: any) => {
  // Reject any client-side tenantId leak before the request leaves
  // the wrapper. See src/services/graphql/assertNoTenantId.ts for
  // the contract — symmetric to the BE rule "never accept tenantId
  // from client input."
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

const MAX_AVATAR_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

interface ActivatePageTwoProps {
  pageTwoStateChangeHandler: () => void;
  cognitologin?: CognitoLoginClass;
  displayName?: string;
}

export default function ActivatePageTwo({
  pageTwoStateChangeHandler,
  displayName,
}: ActivatePageTwoProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFileChange = useCallback((fileEvent: React.ChangeEvent<HTMLInputElement>) => {
    const file = fileEvent.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setErrorMessage("");
      return;
    }

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setErrorMessage("Please select a PNG, JPEG, GIF, or WebP image.");
      return;
    }

    if (file.size > MAX_AVATAR_FILE_SIZE_BYTES) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setErrorMessage("Image must be smaller than 5 MB.");
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setErrorMessage("");
  }, []);

  const handleContinue = useCallback(async () => {
    if (!selectedFile) {
      // No avatar — skip and proceed
      pageTwoStateChangeHandler();
      return;
    }

    setUploading(true);
    setErrorMessage("");

    try {
      // Get presigned PUT URL
      const uploadUrlResponse = await API.graphql(
        graphqlOperation(getFileUploadUrl, {
          input: { fileType: "Avatar", contentType: selectedFile.type },
        })
      );

      const uploadData = getGraphData(uploadUrlResponse, "getFileUploadUrl");
      if (!uploadData?.uploadUrl || !uploadData?.fileKey) {
        setUploading(false);
        setErrorMessage("Failed to get upload URL. Please try again.");
        return;
      }

      // Upload to S3
      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: "PUT",
        body: selectedFile,
        headers: { "Content-Type": selectedFile.type },
      });

      if (!uploadResponse.ok) {
        setUploading(false);
        setErrorMessage("Failed to upload image. Please try again.");
        return;
      }

      // Save the S3 key as the user's avatar
      await API.graphql(
        graphqlOperation(changeAvatar, { input: { avatar: uploadData.fileKey } })
      );

      pageTwoStateChangeHandler();
    } catch (uploadError) {
      console.error("Avatar upload failed:", uploadError);
      setUploading(false);
      setErrorMessage("Something went wrong. Please try again.");
    }
  }, [selectedFile, pageTwoStateChangeHandler]);

  return (
    <>
      {errorMessage && (
        <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
          {errorMessage}
        </div>
      )}

      <div className="space-y-5">
        {/* Avatar preview + file picker. The circle itself is a clickable
            upload control (keyboard-accessible), and the text link below does
            the same — both open the native file dialog. */}
        <div className="flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            aria-label={previewUrl ? "Change avatar" : "Upload an avatar"}
            className="group relative rounded-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 disabled:cursor-not-allowed"
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Avatar preview"
                className="h-20 w-20 rounded-full object-cover ring-2 ring-white/10 transition group-hover:ring-white/25"
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-white/[0.06] border border-white/[0.08] flex items-center justify-center transition group-hover:bg-white/[0.1] group-hover:border-white/20">
                <Upload className="h-6 w-6 text-white/20 transition group-hover:text-white/40" />
              </div>
            )}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(",")}
            onChange={handleFileChange}
            disabled={uploading}
            className="hidden"
          />

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="text-sm text-white/40 hover:text-white/70 transition-colors cursor-pointer"
          >
            {previewUrl ? "Change avatar" : "Choose an avatar (optional)"}
          </button>

          <p className="text-xs text-white/30">{AVATAR_UPLOAD_HINT}</p>
        </div>

        <Button
          onClick={handleContinue}
          disabled={uploading}
          className="w-full h-10 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors"
        >
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : selectedFile ? (
            "Upload & continue"
          ) : (
            "Skip for now"
          )}
        </Button>
      </div>
    </>
  );
}
